// frontend/src/components/ProjectLinkButton.js
import React, { useRef } from 'react';

const LONG_PRESS_DELAY = 600; // milliseconds

const ProjectLinkButton = ({
  icon: Icon,
  label,
  url,
  onEdit,
  onEmpty, // sans lien : clic court (ex. créer le channel Slack) ; sinon onEdit
  badge, // texte d'alerte : petit point orange sur le bouton
  size = 'md',
  variant = 'light',
  className = '',
}) => {
  const timeoutRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const startPress = (event) => {
    if (onEdit) {
      longPressTriggeredRef.current = false;
      timeoutRef.current = window.setTimeout(() => {
        longPressTriggeredRef.current = true;
        onEdit();
      }, LONG_PRESS_DELAY);
    }
  };

  const clearPress = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const stopEventPropagation = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const finishPress = (event) => {
    clearPress();
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (url) {
      const autoClose = typeof url === 'string' && url.includes('slack.com');
      const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (autoClose && openedWindow) {
        const closeTimers = [600, 1200, 1800, 2400];
        closeTimers.forEach((delay) => {
          window.setTimeout(() => {
            if (!openedWindow || openedWindow.closed) {
              return;
            }
            try {
              openedWindow.close();
            } catch {
              // ignore cross-origin
            }
          }, delay);
        });
      }
    } else if (onEmpty || onEdit) {
      (onEmpty || onEdit)();
    }
    longPressTriggeredRef.current = false;
  };

  const sizeClasses = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  const iconClasses = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const hasUrl = Boolean(url);

  const toneClasses = (() => {
    const hasValue = hasUrl;
    if (variant === 'dark') {
      return hasValue
        ? 'text-white border-white/50 bg-white/10 hover:bg-white/20'
        : 'text-white/50 border-white/30 bg-white/5 hover:bg-white/15';
    }
    return hasValue
      ? 'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100'
      : 'text-gray-400 border-gray-200 bg-gray-50 hover:bg-gray-100';
  })();

  const focusRingClasses =
    variant === 'dark'
      ? 'focus:ring-white/60 focus:ring-offset-0'
      : 'focus:ring-blue-500 focus:ring-offset-2';

  return (
    <button
      type="button"
      onMouseDown={startPress}
      onMouseUp={finishPress}
      onMouseLeave={clearPress}
      onTouchStart={(event) => {
        stopEventPropagation(event);
        startPress(event);
      }}
      onTouchEnd={(event) => {
        stopEventPropagation(event);
        finishPress(event);
      }}
      onTouchCancel={clearPress}
      onClick={stopEventPropagation}
      title={
        badge || (hasUrl
          ? `${label} : clic pour ouvrir, appui long pour modifier`
          : onEmpty ? `${label} : clic pour créer ou lier` : `${label} : appui long pour ajouter un lien`)
      }
      className={[
        'relative flex items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus:ring-2',
        focusRingClasses,
        toneClasses,
        sizeClasses,
        className,
      ].join(' ')}
    >
      <Icon className={`${iconClasses}`} />
      {badge && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-white bg-amber-400" />}
    </button>
  );
};

export default ProjectLinkButton;
